import { ChevronLeft, ChevronRight, Megaphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/v2/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/v2/carousel";
import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { useCarousel } from "@/components/ui/v2/use-carousel";
import { ROUTES } from "@/constants/routes";
import { useCampaigns } from "@/features/campaigns/api/useCampaigns";
import { translateIntegrationDisplayName } from "@/features/integrations/utils/translate-integration-display-name";
import { cn } from "@/lib/utils/cn";
import { CampaignTypeDTO } from "@/types/response/CampaignType";

export function ActiveCampaignsSection({ clientId }: { clientId?: string }) {
  const { t } = useTranslation();

  const { data, isLoading } = useCampaigns({
    page: 1,
    pageSize: 12,
    includeIntegrations: true,
    statuses: ["active"],
    clientId,
  });

  const campaigns = data?.data ?? [];

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        <SectionHeader count={0} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[160px] w-full" />
          ))}
        </div>
      </section>
    );
  }

  if (campaigns.length === 0) {
    return (
      <section className="flex flex-col gap-4">
        <SectionHeader count={0} />
        <Card padding="md">
          <CardContent className="px-0">
            <EmptyState
              icon={Megaphone}
              title={t("home.activeCampaigns.empty.title")}
              description={t("home.activeCampaigns.empty.description")}
              action={{
                label: t("home.activeCampaigns.viewAll"),
                href: clientId
                  ? `/${ROUTES.clients}/${clientId}/${ROUTES.campaigns}`
                  : `/${ROUTES.campaigns}`,
              }}
            />
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <Carousel
      opts={{ align: "start", containScroll: "trimSnaps" }}
      className="flex flex-col gap-4"
    >
      <SectionHeader count={campaigns.length} withControls />
      <CarouselContent className="-ml-4">
        {campaigns.map((campaign) => (
          <CarouselItem
            key={campaign.id}
            className="pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
          >
            <CampaignCard campaign={campaign} clientId={clientId} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
}

function SectionHeader({
  count,
  withControls = false,
}: {
  count: number;
  withControls?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <header className="flex items-center justify-between gap-4">
      <h2 className="text-lg font-semibold text-foreground">
        {t("home.activeCampaigns.title")}
      </h2>
      <div className="flex items-center gap-2">
        <Badge variant="success" size="medium">
          {t("home.activeCampaigns.activeCount", { count })}
        </Badge>
        {withControls && <ScrollControls />}
      </div>
    </header>
  );
}

function ScrollControls() {
  const { t } = useTranslation();
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();
  return (
    <>
      <Button
        variant="outline"
        size="icon-sm"
        onClick={scrollPrev}
        aria-label={t("home.activeCampaigns.scrollPrev")}
        disabled={!canScrollPrev}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        onClick={scrollNext}
        aria-label={t("home.activeCampaigns.scrollNext")}
        disabled={!canScrollNext}
      >
        <ChevronRight className="size-4" />
      </Button>
    </>
  );
}

function CampaignCard({
  campaign,
  clientId,
}: {
  campaign: CampaignTypeDTO;
  clientId?: string;
}) {
  const { t, i18n } = useTranslation();
  const integrations = campaign.integrations ?? [];
  const activeIntegrations = integrations.filter(
    (i) => i.isActive && !i.isExpired && !i.isPaused,
  );
  const visible = activeIntegrations.slice(0, 2);
  const overflow = activeIntegrations.length - visible.length;
  const href = clientId
    ? `/${ROUTES.clients}/${clientId}/${ROUTES.campaigns}/${campaign.id}`
    : `/${ROUTES.campaigns}/${campaign.id}`;

  return (
    <Link
      to={href}
      aria-label={t("home.activeCampaigns.openCampaign", { name: campaign.name })}
      className={cn(
        "block h-full rounded-lg outline-none transition",
        "focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <Card
        padding="md"
        hover
        className="flex h-full flex-col gap-4 py-5"
      >
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 px-5">
          <CardTitle className="truncate text-base">{campaign.name}</CardTitle>
          {activeIntegrations.length > 0 && (
            <Badge variant="success" size="small">
              {t("home.activeCampaigns.statusActive")}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-3 px-5 text-sm">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-muted-foreground">
              {t("home.activeCampaigns.segment")}
            </span>
            <span className="truncate font-medium text-foreground">
              {campaign.segmentName ?? "—"}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-muted-foreground">
              {t("home.activeCampaigns.jobboards")}
            </span>
            <span className="font-medium text-foreground tabular-nums">
              {activeIntegrations.length}
            </span>
          </div>
          {visible.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {visible.map((i) => (
                <Badge
                  key={i.clientIntegrationModuleId}
                  variant="outline"
                  size="small"
                >
                  {translateIntegrationDisplayName(i.integrationDisplayName, t, i18n) || "—"}
                </Badge>
              ))}
              {overflow > 0 && (
                <Badge variant="outline" size="small">
                  +{overflow}
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
